
import { Injectable, QueryList } from "@angular/core";
import { get, isEmpty, isEqual } from "lodash";
import { Observable } from "rxjs";
import { delay, filter, tap } from "rxjs/operators";
import { CommonFormManagerDirective } from "../form-manager.directive";
import { ToggleFieldGroupComponent } from "./field-toggle-group.component";
import { ToggleControlComponent } from "./toggle-control.component";

@Injectable()
export class FieldToggleManager {
    public state$: Observable<any>;
    public formState: any;

    constructor(private hostRef: ToggleFieldGroupComponent,
                private formManager: CommonFormManagerDirective) {
        this.state$ = this.formManager.stateReplaySubject$.pipe(
            filter((state: any) => !!state.formData),
            tap((state) => this.formState = state),
            delay(1));
    }

    public get allToggleFieldProps(): string[] {
        return this.hostRef.toggleFieldProps;
    }

    public get toggleControls(): QueryList<ToggleControlComponent> {
        return this.hostRef.toggleControls;
    }

    public getToggleFieldsDataFromForm(state?): any {
        const formState = !!state ? state : this.formState;
        return this.allToggleFieldProps.map((toggleFieldProp) => {
            return get(formState, ["formData", ...toggleFieldProp.split("::")]);
        });
    }

    public toggleNextState(): void {
        this.toggleControls
            .filter((toggleControl) => toggleControl.disableToggle)
            .forEach((toggleControl) => toggleControl.toggleState = true);
    }

    public mapReduceFieldPropsToValues(fieldProps: string[]): any[] {
        return fieldProps.map((fieldProp) => {
            const fieldPropPath = fieldProp.split("::");
            const fieldValue = get(this.formState, ["formData", ...fieldPropPath]);
            return fieldValue;
        });
    }

    public allFieldValues(allFieldProps): any[] {
        return this.mapReduceFieldPropsToValues(allFieldProps);
    }

    public get allToggleFieldValues(): any[] {
        return this.mapReduceFieldPropsToValues(this.allToggleFieldProps);
    }

    public get toggleFieldsAreEmpty(): boolean {
        return this.allFieldsAreEmpty(this.allToggleFieldProps);
    }

    public allFieldsAreEmpty(allFieldProps): boolean {
        return this.allToggleFieldValues.every((fieldValue) => {
            if (Array.isArray(fieldValue) || typeof fieldValue === "object") {
                return isEmpty(fieldValue);
            } else {
                return !fieldValue;
            }
        });
    }

    public distinctUntilChangedFactory(allFieldProps): (x: any, y: any) => boolean {
        return (stateA, stateB) => {
            if (isEmpty(allFieldProps)) {
                return true;
            }
            const stateAValues = {};
            const stateBValues = {};
            allFieldProps.forEach((fieldProp) => {
                stateAValues[fieldProp] = get(stateA, ["formData", ...fieldProp.split("::")]);
                stateBValues[fieldProp] = get(stateB, ["formData", ...fieldProp.split("::")]);
            });
            return isEqual(stateAValues, stateBValues);
        };
    }
}
